import { Button } from "@/components/ui/button";
import {
    ArDateFormField,
    ArForm,
    ArFormField,
    ArNumberFormField,
} from "@/v2/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import { Plus, Trash2 } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
    CreateQuotationSchema,
    QuotationFormSchema,
} from "@/v2/schema/quotation";
import CreateQuotation from "@/v2/api/quotation/Create";
import UpdateQuotation from "@/v2/api/quotation/Update";
import FindOneQuotation from "@/v2/api/quotation/FindOne";
import { ProjectComboBox } from "../../ui/combo-box/Project";
import { Project } from "@/v2/types";
import TermsAndConditionFormField from "../../ui/form/TermsAndCondition";
import { useNavigate } from "react-router-dom";
import { ClientComboBox } from "../../ui/combo-box";

export default function QuotationForm({ id }: { id?: string }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [selectedProject, setSelectedProject] = useState<Project | null>(
        null,
    );

    const query = useQuery({
        queryKey: ["quotations", id],
        queryFn: () => FindOneQuotation(id!),
        enabled: !!id,
    });

    const form = useForm<QuotationFormSchema>({
        resolver: zodResolver(QuotationFormSchema),
        defaultValues: {
            reference_number: "",
            project_id: null,
            project_name: "",
            quotation_to_id: "",
            items: [
                {
                    sno: 1,
                    description: "",
                    quantity: 0,
                    tax: 15,
                    uom: "",
                    unit_price: 0,
                },
            ],
            recipient_name: "",
            recipient_designation: "",
            subject: "",
            terms_and_conditions: [{ value: "" }],
            quotation_date: "",
            location: "",
        },
    });

    const { reset, control, setValue, watch } = form;

    const {
        fields: itemFields,
        append: appendItem,
        remove: removeItem,
    } = useFieldArray({
        control,
        name: "items",
    });

    const {
        fields: termsFields,
        append: appendTerm,
        remove: removeTerm,
    } = useFieldArray({
        control,
        name: "terms_and_conditions",
    });

    useEffect(() => {
        if (query?.data) {
            reset({
                quotation_date: query?.data?.quotation_date || "",
                reference_number: query?.data?.reference_number || "",
                quotation_to_id: query?.data?.quotation_to_id || "",
                project_id: query?.data?.project_id || null,
                project_name: query?.data?.project_name || "",
                items: query?.data?.items || [
                    {
                        sno: 1,
                        description: "",
                        quantity: 0,
                        uom: "",
                        unit_price: 0,
                    },
                ],
                recipient_name: query?.data?.recipient_name || "",
                recipient_designation: query?.data?.recipient_designation || "",
                subject: query?.data?.subject || "",
                terms_and_conditions: query?.data?.terms_and_conditions?.map(
                    (term: string) => ({
                        value: term,
                    }),
                ) || [{ value: "" }],
                location: query?.data?.location || "",
            });
        }
    }, [query?.data, reset]);

    useEffect(() => {
        if (selectedProject) {
            setValue("project_name", selectedProject.name);
            setValue("location", selectedProject.location);
            setValue("quotation_to_id", selectedProject.client_id);
        }
    }, [selectedProject]);

    const CreateMutation = useMutation({
        mutationFn: (data: CreateQuotationSchema) => CreateQuotation(data),
    });

    const EditMutation = useMutation({
        mutationFn: (data: CreateQuotationSchema) =>
            UpdateQuotation({ id: id!, data: data }),
    });

    const handleFormSubmit = (data: QuotationFormSchema) => {
        const mutation = id ? EditMutation : CreateMutation;

        const action = id ? "updated" : "generated";

        const transformedData = {
            ...data,
            terms_and_conditions: data.terms_and_conditions.map(
                (item) => item.value,
            ),
        };

        mutation.mutate(transformedData, {
            onError: (err) => {
                console.error({ err });
                toast.error("Oops! Something went wrong", {
                    description: `While ${id ? "updating" : "generating"} the purchase order, we encountered an issue.`,
                });
            },
            onSuccess: (res) => {
                toast.success(`Quotation ${action} successfully`);
                queryClient.invalidateQueries({
                    queryKey: ["purchase-orders"],
                });
                navigate(`/quotation/${res.id}`);
            },
        });
    };

    if (id && query.isLoading) {
        return <LoadingAnimation />;
    }

    if (id && query.isError) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">
                        Failed to load purchase order
                    </p>
                </div>
            </div>
        );
    }

    const items = watch("items");

    const calculateItemAmount = (index: number) => {
        const item = items[index];
        if (!item) return { withTax: 0, withoutTax: 0 };

        const quantity = item.quantity || 1;
        const unitPrice = item.unit_price || 0;
        const taxRate = item.tax || 15;

        const withoutTax = quantity * unitPrice;
        const withTax = withoutTax * (1 + taxRate / 100);

        return { withTax, withoutTax };
    };
    const calculateGrandTotal = () => {
        const items = form.watch("items");

        let grossTotal = 0;
        let totalTax = 0;

        items.forEach((item) => {
            const quantity = item.quantity || 0;
            const unitPrice = item.unit_price || 0;
            const taxRate = item.tax || 15;

            const subtotal = quantity * unitPrice;
            const tax = subtotal * (taxRate / 100);

            grossTotal += subtotal;
            totalTax += tax;
        });

        const netTotal = grossTotal + totalTax;

        return { grossTotal, totalTax, netTotal };
    };

    return (
        <ArForm
            form={form}
            formSchema={QuotationFormSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArDateFormField<QuotationFormSchema>
                        label="Date"
                        name="quotation_date"
                    />

                    <ProjectComboBox
                        name="project_id"
                        setSelected={setSelectedProject}
                        defaultValue={query?.data?.project || null}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ClientComboBox<QuotationFormSchema>
                        name="quotation_to_id"
                        defaultValue={query?.data?.client || null}
                    />

                    <ArFormField<QuotationFormSchema>
                        label="Project Name"
                        name="project_name"
                        placeholder="Enter project name"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<QuotationFormSchema>
                        label="Project Location"
                        name="location"
                        placeholder="Enter location"
                    />

                    <ArFormField<QuotationFormSchema>
                        label="Reference Number"
                        name="reference_number"
                        placeholder="Enter reference number"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<QuotationFormSchema>
                        label="Recipient"
                        name="recipient_name"
                        placeholder="Enter recipient name"
                    />
                    <ArFormField<QuotationFormSchema>
                        label="Recipient Designation"
                        name="recipient_designation"
                        placeholder="Enter designation"
                    />
                </div>

                <ArFormField<QuotationFormSchema>
                    label="Subject"
                    name="subject"
                    placeholder="Enter subject"
                />

                <Card className="p-4">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Items</h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    appendItem({
                                        sno: itemFields.length + 1,
                                        description: "",
                                        quantity: 0,
                                        uom: "",
                                        tax: 15,
                                        unit_price: 0,
                                    })
                                }
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add Item
                            </Button>
                        </div>

                        {itemFields.map((field, index) => {
                            const { withoutTax, withTax } =
                                calculateItemAmount(index);
                            return (
                                <div
                                    key={field.id}
                                    className="space-y-4 rounded-lg border p-4"
                                >
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-8">
                                        <div className="col-span-3 flex w-full items-start gap-3">
                                            <span className="flex h-full items-center py-2">
                                                {index + 1}
                                            </span>
                                            <div className="w-full">
                                                <ArFormField<QuotationFormSchema>
                                                    label="Description"
                                                    name={`items.${index}.description`}
                                                    placeholder="Enter item description"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-span-2 grid grid-cols-2 gap-4">
                                            <ArNumberFormField<QuotationFormSchema>
                                                label="Unit Price"
                                                name={`items.${index}.unit_price`}
                                                placeholder="0.00"
                                            />

                                            <ArFormField<QuotationFormSchema>
                                                label="Unit"
                                                name={`items.${index}.uom`}
                                            />
                                        </div>

                                        <div className="col-span-1 grid grid-cols-2 gap-4">
                                            <ArNumberFormField<QuotationFormSchema>
                                                label="Quantity"
                                                name={`items.${index}.quantity`}
                                                placeholder="0"
                                            />

                                            <ArNumberFormField<QuotationFormSchema>
                                                label="Tax (%)"
                                                name={`items.${index}.tax`}
                                                placeholder="0"
                                            />
                                        </div>
                                        <div className="col-span-2 flex h-full items-start justify-end gap-3">
                                            <div className="flex flex-col items-end gap-2">
                                                <Label className="">
                                                    Amount
                                                </Label>
                                                <Label className="font-bold">
                                                    {withoutTax.toFixed(2)} SR
                                                </Label>
                                                <Label className="flex gap-2">
                                                    <span className="text-muted-foreground">
                                                        With Tax :
                                                    </span>
                                                    {withTax.toFixed(2)} SR
                                                </Label>
                                            </div>
                                            <div className="flex h-full items-center">
                                                {itemFields.length > 1 && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            removeItem(index)
                                                        }
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="flex justify-end border-t pt-4">
                            <div className="w-full max-w-sm space-y-3 rounded-lg bg-gray-50/50 p-6">
                                <div className="flex justify-between text-base">
                                    <span className="text-gray-700">
                                        Gross Total:
                                    </span>
                                    <span className="font-semibold">
                                        {calculateGrandTotal().grossTotal.toFixed(
                                            3,
                                        )}{" "}
                                        SR
                                    </span>
                                </div>
                                <div className="flex justify-between text-base">
                                    <span className="text-gray-700">
                                        Total Tax:
                                    </span>
                                    <span className="font-semibold text-amber-600">
                                        {calculateGrandTotal().totalTax.toFixed(
                                            3,
                                        )}{" "}
                                        SR
                                    </span>
                                </div>
                                <div className="flex justify-between border-t pt-3 text-lg">
                                    <span className="font-semibold">
                                        Net Total:
                                    </span>
                                    <span className="font-bold text-primary">
                                        {calculateGrandTotal().netTotal.toFixed(
                                            3,
                                        )}{" "}
                                        SR
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                <TermsAndConditionFormField<QuotationFormSchema>
                    name="terms_and_conditions"
                    label="Terms"
                    append={appendTerm}
                    remove={removeTerm}
                    termsFields={termsFields}
                    placeholder="Enter term or condition"
                />

                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            CreateMutation.isPending || EditMutation.isPending
                        }
                    >
                        {CreateMutation.isPending || EditMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Quotation"
                              : "Create Quotation"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
